/**
 * Cifrado de extremo a extremo — lado del navegador.
 *
 * Usa la Web Crypto API nativa: sin dependencias, y las operaciones ocurren
 * fuera de JavaScript, así que la clave nunca se expone como un array que
 * cualquier script pueda leer.
 *
 *   · RSA-OAEP (SHA-256) — sólo para entregar la clave de sesión (handshake).
 *   · AES-256-GCM        — para todos los cuerpos de petición y respuesta.
 *
 * DÓNDE VIVE LA CLAVE: en una variable dentro del closure de `instalarCifrado`.
 * **Nunca** en localStorage, sessionStorage ni cookies. Si se cierra la
 * pestaña, desaparece. Cada pestaña genera la suya, así que comprometer una
 * no compromete las demás.
 *
 * QUÉ PROTEGE ESTO QUE NO PROTEJA YA HTTPS: el tramo TLS acaba en el borde de
 * la red (un proxy corporativo, un balanceador, una extensión del navegador
 * que lea `fetch`). A partir de ahí, y en cualquier registro intermedio, el
 * cuerpo iría en claro. Con esto, lo que se ve en las herramientas de
 * desarrollo o en un proxy es un sobre ilegible. Y el tag de GCM detecta
 * cualquier alteración del contenido en el camino.
 */

/* ─── Clave pública RSA ──────────────────────────────────────────────────────
 *
 * Va escrita aquí a propósito, no se pide al servidor. Una clave pública no
 * es un secreto, así que publicarla no es el problema; el problema sería de
 * dónde la toma el navegador. Si la pidiera por un endpoint, quien pudiera
 * responder en lugar del servidor —un proxy, una red hostil— devolvería SU
 * clave, el navegador cifraría la clave de sesión con ella y ese atacante
 * leería todo el tráfico. Fijarla en el código elimina ese vector.
 *
 * Se regenera con `npm run claves:rsa` en el backend; hay que desplegar los
 * dos lados a la vez.
 */
const CLAVE_PUBLICA_RSA_PEM = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAk93dwauujuYSC4ERBcsu
RXvplxm6wmftM1KrOd9SThMfAQtqgzuR46EL4txNogVC+Y4SCjWFW3JqdZEbJJRQ
GSu35GUUEzElimVC/nXJiyODqM4v88kxfaW/zzP34VMhroytC2/X4arw3JS9CD+R
zE/izAEaghFY+5eNFOKS0iJRzjJkQVPe/AYAqNRR/AGGrA/FtPRLLjbLlACOAEAD
elyLfwplmiuMaVdcUmExMZVkVypyyp4azzlxYZo1aKnzdHwVR9DGuoQ7hAw8wApf
jM7xYUEiDFfNZaJe+mhMWDGT2sHjCDqcWVruGkPbXpdyf96sIK3+Zo8Ihf92J4Ex
WQIDAQAB
-----END PUBLIC KEY-----`;

/** 440 «Login Time-out»: el backend perdió la sesión y hay que rehacerla. */
const SESION_VENCIDA = 440;

// ─── Conversiones ────────────────────────────────────────────────────────────

const pemABuffer = (pem) => {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s/g, '');
  const binario = atob(b64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes.buffer;
};

const aBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binario = '';
  // De 8 kB en 8 kB: `String.fromCharCode(...bytes)` de golpe revienta la pila
  // con cuerpos grandes (un listado de visitas del mes lo tiene).
  for (let i = 0; i < bytes.length; i += 8192) {
    binario += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
  }
  return btoa(binario);
};

const desdeBase64 = (b64) => {
  const binario = atob(b64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes.buffer;
};

// ─── RSA ─────────────────────────────────────────────────────────────────────

let promesaClavePublica = null;

const importarClavePublica = () => {
  if (!promesaClavePublica) {
    promesaClavePublica = crypto.subtle.importKey(
      'spki',
      pemABuffer(CLAVE_PUBLICA_RSA_PEM),
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    );
  }
  return promesaClavePublica;
};

const cifrarClaveDeSesion = async (bytes) => {
  const publica = await importarClavePublica();
  return aBase64(await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publica, bytes));
};

// ─── AES-256-GCM ─────────────────────────────────────────────────────────────

// `extractable: false`: ni siquiera nuestro propio código puede volver a leer
// los bytes de la clave una vez importada.
const importarClaveAes = (bytes) =>
  crypto.subtle.importKey('raw', bytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);

const cifrarAesGcm = async (datos, claveAes) => {
  const texto = typeof datos === 'string' ? datos : JSON.stringify(datos);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const salida = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      claveAes,
      new TextEncoder().encode(texto)
    )
  );

  // La Web Crypto devuelve ciphertext+tag pegados; Node los quiere separados.
  const corte = salida.length - 16;
  return {
    v: 1,
    iv: aBase64(iv.buffer),
    data: aBase64(salida.slice(0, corte).buffer),
    tag: aBase64(salida.slice(corte).buffer),
  };
};

const descifrarAesGcm = async (sobre, claveAes) => {
  const cifrado = new Uint8Array(desdeBase64(sobre.data));
  const tag = new Uint8Array(desdeBase64(sobre.tag));

  // Se vuelven a pegar para la Web Crypto.
  const juntos = new Uint8Array(cifrado.length + tag.length);
  juntos.set(cifrado, 0);
  juntos.set(tag, cifrado.length);

  const plano = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(desdeBase64(sobre.iv)), tagLength: 128 },
    claveAes,
    juntos.buffer
  );

  const texto = new TextDecoder().decode(plano);
  try {
    return JSON.parse(texto);
  } catch {
    return texto;
  }
};

const esSobreCifrado = (valor) =>
  !!valor &&
  typeof valor === 'object' &&
  valor.v === 1 &&
  typeof valor.iv === 'string' &&
  typeof valor.data === 'string' &&
  typeof valor.tag === 'string';

// ─── Instalación sobre una instancia de axios ────────────────────────────────

/**
 * Instala el cifrado en una instancia de axios.
 *
 * ⚠️ DEBE LLAMARSE ANTES DE REGISTRAR LOS DEMÁS INTERCEPTORES, y el motivo es
 * el orden en que axios los ejecuta:
 *   · los de PETICIÓN se apilan al revés (el último registrado corre primero),
 *     así que registrando éste primero se ejecuta el ÚLTIMO — cifra el cuerpo
 *     cuando el token y la clínica ya se pusieron en las cabeceras;
 *   · los de RESPUESTA corren en orden de registro, así que éste corre PRIMERO
 *     y descifra antes de que el manejador de errores lea `data.error`.
 *
 * @param {import('axios').AxiosInstance} instancia
 */
export const instalarCifrado = (instancia) => {
  let claveAes = null;   // CryptoKey, no extraíble
  let bytesClave = null; // sólo para calcular la huella; se descarta después
  let huella = null;
  let promesaHandshake = null;

  const calcularHuella = async (bytes) => {
    const resumen = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
    let hex = '';
    for (let i = 0; i < resumen.length; i++) hex += resumen[i].toString(16).padStart(2, '0');
    return hex.substring(0, 16);
  };

  const hacerHandshake = async () => {
    bytesClave = crypto.getRandomValues(new Uint8Array(32));
    huella = await calcularHuella(bytesClave);

    const claveCifrada = await cifrarClaveDeSesion(bytesClave);

    // Se manda con `fetch` y no con axios a propósito: si pasara por esta
    // misma instancia, el interceptor de petición esperaría a que el
    // handshake terminase… para poder mandar el handshake. Interbloqueo.
    const respuesta = await fetch(`${instancia.defaults.baseURL}/handshake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ encryptedKey: claveCifrada }),
    });

    if (!respuesta.ok) throw new Error(`El handshake falló con HTTP ${respuesta.status}`);

    const resultado = await respuesta.json();
    if (!resultado.success) throw new Error('El servidor rechazó el handshake');

    // Si el servidor derivó otra huella, la clave no llegó íntegra: seguir
    // sería cifrar contra una clave distinta y fallar en cada petición.
    if (resultado.keyId && resultado.keyId !== huella) {
      throw new Error('La huella de la sesión no coincide con la del servidor');
    }

    claveAes = await importarClaveAes(bytesClave);
    bytesClave = null; // ya no hace falta; fuera de la memoria accesible
  };

  const asegurarHandshake = () => {
    if (!promesaHandshake) {
      promesaHandshake = hacerHandshake().catch((error) => {
        // Se limpia para que el siguiente intento vuelva a probar en vez de
        // quedarse pegado a una promesa ya rechazada.
        promesaHandshake = null;
        claveAes = null;
        bytesClave = null;
        huella = null;
        throw error;
      });
    }
    return promesaHandshake;
  };

  instancia.interceptors.request.use(async (config) => {
    await asegurarHandshake();

    config.headers['X-Encrypted'] = 'true';
    config.headers['X-Key-Id'] = huella;

    // Un FormData (subida de archivos) se deja intacto: va como multipart y el
    // backend lo necesita tal cual para que multer lo procese. La respuesta sí
    // vuelve cifrada.
    const esFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;

    if (config.data && typeof config.data === 'object' && !esFormData && !esSobreCifrado(config.data)) {
      // Se guarda el original por si hay que reintentar tras un 440: para
      // entonces el cuerpo ya estaría cifrado con la clave vieja.
      config.datosOriginales = config.data;
      config.data = await cifrarAesGcm(config.data, claveAes);
    }

    return config;
  });

  instancia.interceptors.response.use(
    async (respuesta) => {
      if (esSobreCifrado(respuesta.data) && claveAes) {
        respuesta.data = await descifrarAesGcm(respuesta.data, claveAes);
      }
      return respuesta;
    },
    async (error) => {
      // Sesión vencida: el backend se reinició o pasaron 24 h. Se rehace el
      // handshake y se reintenta una sola vez, de forma que el usuario no se
      // entera. `_reintentado` evita el bucle si el 440 persiste.
      if (error.response?.status === SESION_VENCIDA && error.config && !error.config._reintentado) {
        error.config._reintentado = true;
        promesaHandshake = null;
        claveAes = null;
        huella = null;

        try {
          await asegurarHandshake();
          if (error.config.datosOriginales) {
            error.config.data = error.config.datosOriginales;
            delete error.config.datosOriginales;
          }
          return instancia.request(error.config);
        } catch {
          // El handshake volvió a fallar: cae al manejo normal de errores.
        }
      }

      // Los errores también vienen cifrados; hay que abrirlos antes de que el
      // interceptor siguiente lea `error.response.data.error`.
      if (esSobreCifrado(error.response?.data) && claveAes) {
        try {
          error.response.data = await descifrarAesGcm(error.response.data, claveAes);
        } catch {
          // Si no se puede descifrar se deja como está: mejor un error opaco
          // que tragarse el fallo original.
        }
      }

      return Promise.reject(error);
    }
  );
};

export default instalarCifrado;
